import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ChartData } from 'chart.js';
import React, { Component, FormEvent } from 'react';
import LineChartComponent from '../../../chart/charts/LineChartComponent';
import CustomLoading from '../../../custom/CustomLoading';
import AiModelResultChartContainer from './AiModelResultChartContainer';
import MatrixChart from './MatrixChart';
import { MLOpsMetricResultType } from '../../../../domain/mlops/MLOpsDomains';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const commonChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  elements: {
    line: {
      tension: 0.8,
      borderWidth: 3,
      fill: false,
      cubicInterpolationMode: 'monotone',
    },
    point: {
      radius: 0,
      hitRadius: 10,
      hoverRadius: 4,
      hoverBorderWidth: 2,
    },
  },
  scales: {
    x: {
      type: 'linear',
      grid: { display: false },
      ticks: {
        maxRotation: 0,
        minRotation: 0,
        font: { size: 10 },
        padding: 5,
        //@ts-ignore
        callback: function (value: any) {
          // @ts-ignore
          const label = this.getLabelForValue(value);
          return label.length > 10 ? label.substr(0, 15) : label;
        },
      },
      afterFit: (scale: any) => {
        scale.height = 30;
      },
    },
    y: {
      type: 'linear',
      grid: {
        color: '#E0E4E8',
        drawBorder: false,
      },
      ticks: { padding: 8 },
    },
  },
  plugins: {
    legend: {
      display: false,
      position: 'top',
      labels: {
        usePointStyle: true,
        padding: 15,
        font: { size: 12 },
      },
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleFont: { size: 13 },
      bodyFont: { size: 12 },
      padding: 12,
      displayColors: true,
    },
  },
  layout: {
    padding: { bottom: 0 },
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  animation: {
    duration: 1000,
    easing: 'easeInOutQuart',
  },
};

class MlOpsResultChartComponent extends Component<Props, State> {
  public state = new State();
  private timeoutId: NodeJS.Timeout | undefined;

  render() {
    const { isLoading, metrics, confusionMatrixData } = this.props;
    const { threshold } = this.state;
    if (isLoading) {
      return <CustomLoading />;
    }

    if (!metrics || metrics.size === 0) {
      return <div className='text-center text-f-black-3 p-4'>No metrics data available</div>;
    }
    return (
      <div className='grid grid-cols-2 gap-4'>
        <AiModelResultChartContainer title='ROC Curve'>
          <LineChartComponent options={commonChartOptions as any} data={metrics.get(MLOpsMetricResultType.ROC_CURVE)!} />
        </AiModelResultChartContainer>

        <div style={{ boxShadow: '2px 2px 15px 0px #0000000D' }} className='bg-white border border-f-gray-3 rounded-[8px] h-[500px] gap-2 p-2'>
          <div className={'flex w-full items-center justify-between border-b p-2'}>
            <h3 className='text-sm text-f-black-2 font-semibold'>Confusion Matrix</h3>
            <span className={'flex items-center gap-2'}>
              <label htmlFor={'threshold-value'} className={'text-sm text-f-black-2 font-semibold'}>
                Threshold
              </label>
              <input
                id={'threshold-value'}
                max={100}
                min={1}
                className={'border w-[100px] border-f-gray-3 h-7 rounded-lg indent-2'}
                type={'number'}
                value={threshold}
                onChange={(event) => {
                  this.onThresholdChange(event);
                }}
              />
            </span>
          </div>

          <div className='h-[calc(100%-60px)]'>
            {confusionMatrixData && !this.props.thresholdLoading ? (
              <MatrixChart data={confusionMatrixData?.data} labels={confusionMatrixData?.labels} />
            ) : (
              <CustomLoading />
            )}
          </div>
        </div>

        <AiModelResultChartContainer title='FPR/FNR vs Threshold'>
          <LineChartComponent options={commonChartOptions as any} data={metrics.get(MLOpsMetricResultType.FPR_FNR_THRESHOLD)!} />
        </AiModelResultChartContainer>

        <AiModelResultChartContainer title='TPR/TNR vs Threshold'>
          <LineChartComponent options={commonChartOptions as any} data={metrics.get(MLOpsMetricResultType.TPR_TNR_THRESHOLD)!} />
        </AiModelResultChartContainer>

        <AiModelResultChartContainer title='Precision-Recall Curve'>
          <LineChartComponent options={commonChartOptions as any} data={metrics.get(MLOpsMetricResultType.PRECISION_RECALL)!} />
        </AiModelResultChartContainer>

        <AiModelResultChartContainer title='Matthews Correlation Coefficient'>
          <LineChartComponent options={commonChartOptions as any} data={metrics.get(MLOpsMetricResultType.MCC_THRESHOLD)!} />
        </AiModelResultChartContainer>

        <AiModelResultChartContainer title='G-Mean vs Threshold'>
          <LineChartComponent options={commonChartOptions as any} data={metrics.get(MLOpsMetricResultType.GMEAN_THRESHOLD)!} />
        </AiModelResultChartContainer>

        <AiModelResultChartContainer title='F Beta Score vs Threshold'>
          <LineChartComponent options={commonChartOptions as any} data={metrics.get(MLOpsMetricResultType.FBETA_THRESHOLD)!} />
        </AiModelResultChartContainer>

        <AiModelResultChartContainer title='Balanced Accuracy vs Threshold'>
          <LineChartComponent options={commonChartOptions as any} data={metrics.get(MLOpsMetricResultType.BALANCED_ACCURACY_THRESHOLD)!} />
        </AiModelResultChartContainer>

        <AiModelResultChartContainer title='F1 Score vs Threshold'>
          <LineChartComponent options={commonChartOptions as any} data={metrics.get(MLOpsMetricResultType.F1_THRESHOLD)!} />
        </AiModelResultChartContainer>
      </div>
    );
  }

  private onThresholdChange = (e: FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.valueAsNumber > 100 ? 100 : e.currentTarget.valueAsNumber;
    this.setState({ threshold: Number(value) }, this.restartSearch);
  };

  private restartSearch = () => {
    if (this.timeoutId == undefined) {
      this.timeoutId = setTimeout(async () => {
        this.props.onChangeThreshold(this.state.threshold);
      }, 200);
    } else {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
      this.restartSearch();
    }
  };
}

class State {
  threshold: number = 1;
}

interface Props {
  metrics: Map<string, ChartData<'line'>>;
  confusionMatrixData: { data: number[][]; labels: string[] } | null;
  isLoading?: boolean;
  thresholdLoading?: boolean;
  onChangeThreshold: (threshold: number) => void;
}

export default MlOpsResultChartComponent;
